import { Int8BufferAttribute, Vector3 } from "three";
import { EditList } from "../display/EditList.js";
import { MarkerContent } from "../display/MarkerContent.js";
import { MarkerLayer } from "../display/MarkerLayer.js";
import { MarkerList } from "../display/MarkerList.js";
import { Canvas3d } from "../display3d/Canvas3d.js";
import { Control3d } from "../display3d/Control3d.js";
import { Scene3d } from "../display3d/Scene3d.js";
import { Data } from "../model/Data.js";
import { VOMarker } from "../model/DataStructure.js";

export class PolarsternApp {

    private _data: Data;
    private _canvas: Canvas3d;
    private _control: Control3d;
    private _scene: Scene3d;
    private _editList: EditList;
    private _markerLayer: MarkerLayer;
    private _markerContent: MarkerContent;
    private _markerList: MarkerList;
    private _marker: VOMarker = null;

    constructor(private _element: HTMLElement) {
        e5.console.log("[eventfive.awi.polarstern-3d] [PolarsternApp]");

        this.init();
    }

    private async init() {

        this._element.innerHTML = `
        <div class="awi-polarstern-app__content">
            <div class="awi-polarstern-app__canvas-wrapper">
                <div class="awi-polarstern-app__canvas"></div>
                <div class="awi-polarstern-app__marker-layer"></div>
                <div class="awi-polarstern-app__marker-content"></div>
            </div>                
            <div class="awi-polarstern-app__marker-list"></div>
            <div class="awi-polarstern-app__list"></div>
        </div>
        <div class="awi-polarstern-app__preloader">
            <div class="awi-polarstern-app__preloader-box">
                <div class="awi-polarstern-app__preloader-progress-icon">
                    <div class="awi-polarstern-app__preloader-progress-logo"></div>
                </div>
                <div class="awi-polarstern-app__preloader-progress-track">
                    <div class="awi-polarstern-app__preloader-progress-fill"></div>
                </div>
            </div>
        </div>
        `;

        const assetsUrl = this._element.getAttribute("data-base") + "/assets";
        let dataUrl = this._element.getAttribute("data-url");

        let editMode = false;
        e5.Debug.add({
            uid: "awi-marker-edit-mode",
            icon: "fal fa-map-marker-edit",
            cookie: true,
            tooltip: "Edit markers"
        });
        editMode = this._element.getAttribute("data-isloggedin") == "on"
            && (e5.Debug.active("awi-marker-edit-mode")
                || e5.Url.params.get("edit") == 'on');

        if (editMode
            && e5.Url.params.get('scene')) {
            dataUrl = e5.Url.params.set("scene_id", e5.Url.params.get('scene'), dataUrl);
        }

        const data = new Data();
        await data.load(dataUrl);
        this._data = data;


        e5.Debug.add({
            uid: "awi-3d-stuff",
            cookie: true,
            tooltip: "3D Options",
            icon: "fal fa-cube"
        });

        const gui = new dat.GUI();
        gui.useLocalStorage = true;
        if (!e5.Debug.active("awi-3d-stuff")) {
            gui.hide();
        }

        this._canvas = new Canvas3d({
            container: this._element.querySelector(".awi-polarstern-app__canvas"),
            bloom: false,
            gui: gui,
            cameraPosition: new Vector3(
                data.data.scene.camera_x,
                data.data.scene.camera_y,
                data.data.scene.camera_z
            )
        });

        this._control = new Control3d({
            canvas: this._canvas,
            gui: gui,
            blur: editMode ? 0 : 1,
            showTransform: editMode,
            target: new Vector3(
                data.data.scene.target_x,
                data.data.scene.target_y,
                data.data.scene.target_z
            )
        });

        this._scene = new Scene3d({
            assetsUrl: assetsUrl,
            modelUrl: data.data.scene.model.url,
            canvas: this._canvas
        });

        this._markerLayer = new MarkerLayer({
            container: this._element.querySelector(".awi-polarstern-app__marker-layer"),
            canvas: this._canvas,
            markers: this._data.data.markers,
            onSelect: (m: VOMarker) => {
                this.marker = m;
            },
            onDeselect: (m: VOMarker) => {
                this.marker = null;
            }
        });

        this._markerContent = new MarkerContent({
            container: this._element.querySelector(".awi-polarstern-app__marker-content"),
            onClose: () => {
                this.marker = null
            },
            onNext: () => {
                if (!this._marker) {
                    this.marker = data.data.markers[0];
                } else {
                    let index = e5.Calc.wrap(data.data.markers.indexOf(this._marker) + 1, data.data.markers.length);
                    this.marker = data.data.markers[index];
                }
            },
            onPrev: () => {
                if (!this._marker) {
                    this.marker = data.data.markers[0];
                } else {
                    let index = e5.Calc.wrap(data.data.markers.indexOf(this._marker) - 1, data.data.markers.length);
                    this.marker = data.data.markers[index];
                }
            }
        });

        this._markerList = new MarkerList({
            container: this._element.querySelector(".awi-polarstern-app__marker-list"),
            markers: this._data.data.markers,
            onSelect: (m: VOMarker) => {
                this.marker = m;
            },
            onNext: () => {
                if (!this._marker) {
                    this.marker = data.data.markers[0];
                } else {
                    let index = e5.Calc.wrap(data.data.markers.indexOf(this._marker) + 1, data.data.markers.length);
                    this.marker = data.data.markers[index];
                }
            },
            onPrev: () => {
                if (!this._marker) {
                    this.marker = data.data.markers[0];
                } else {
                    let index = e5.Calc.wrap(data.data.markers.indexOf(this._marker) - 1, data.data.markers.length);
                    this.marker = data.data.markers[index];
                }
            }
        });

        if (editMode) {
            this._editList = new EditList({
                container: this._element.querySelector(".awi-polarstern-app__list"),
                control: this._control,
                saveUrl: this._element.getAttribute("data-save"),
                markerLayer: this._markerLayer,
                items: [
                    {
                        label: this._data.data.scene.name,
                        icon: "fal fa-cube",
                        storage: this._data.data.scene
                    },
                    ... this._data.data.markers.map((m) => {
                        return {
                            label: (m.index + 1) + " | " + m.name,
                            icon: "fal fa-map-marker",
                            storage: m
                        };
                    })
                ]
            });
        }

        await this._scene.load((loaded, total) => {
            this._element.querySelector<HTMLElement>(".awi-polarstern-app__preloader-progress-fill")
                .style.width = ((loaded / total) * 100) + "%";
        });

        this._element.querySelector(".awi-polarstern-app__preloader")
            .classList.add("awi-polarstern-app__preloader--hidden");

        await e5.wait(1000);
        this._element.querySelector(".awi-polarstern-app__preloader").remove();

    }

    public get marker(): VOMarker {
        return this._marker;
    }

    public set marker(value: VOMarker) {
        this._control.show(value ? value : this._data.data.scene);
        this._markerContent.marker = value;
        this._markerLayer.marker = value;
        this._markerList.marker = value;
        this._marker = value;


        this._canvas.viewportActive = Boolean(value);

        // this._canvas.viewOffsetX = value ? 200 : 0;
        // gsap.to(this._canvas, {
        //     duration: 0.75,
        //     offsetX: value ? -400 : 0,
        //     offsetY: 0
        // });
    }
}

document.addEventListener("DOMContentLoaded",
    () => {
        document.querySelectorAll<HTMLElement>(".awi-polarstern-app")
            .forEach((e) => {
                new PolarsternApp(e);
            });

    }
);